import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Avatar,
    Divider,
    CircularProgress,
    Chip,
    Button
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { formatDistanceToNow } from 'date-fns';
import he from 'date-fns/locale/he';

const MessagesThread = ({ messages, loading, onSendMessage, onMarkAsRead }) => {
    // Track which messages we've already marked as read
    const markedAsReadRef = useRef(new Set());
    
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return formatDistanceToNow(date, { addSuffix: true, locale: he });
        } catch (e) {
            return dateString;
        }
    };

    const getInitials = (message, isOutgoing) => {
        // For outgoing messages, show "אנ" (אני)
        if (isOutgoing) {
            return 'אנ';
        }
        
        // For incoming messages, try to use dependent's name
        if (message.dependent) {
            const firstName = message.dependent.firstName || '';
            const lastName = message.dependent.lastName || '';
            if (firstName && lastName) {
                return (firstName[0] + lastName[0]).toUpperCase();
            }
            if (firstName) {
                return firstName.substring(0, 2).toUpperCase();
            }
        }
        
        // Fallback to email
        const email = message.fromEmail;
        if (!email) return '??';
        
        const namePart = email.split('@')[0];
        
        // If it contains dots or underscores, try to get first letters
        if (namePart.includes('.') || namePart.includes('_')) {
            const parts = namePart.split(/[._]/);
            return (parts[0][0] + (parts[1]?.[0] || parts[0][1] || '')).toUpperCase();
        }
        
        // Otherwise, take first 2 characters
        return namePart.substring(0, 2).toUpperCase();
    };

    const handleDownloadFile = async (fileUrl, filename) => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        // Mark unread messages as read (only once per message)
        if (messages && messages.length > 0 && onMarkAsRead) {
            messages.forEach(msg => {
                // Only mark if: unread, incoming, and not already marked by us
                if (!msg.isRead && msg.direction === 'incoming' && !markedAsReadRef.current.has(msg.id)) {
                    markedAsReadRef.current.add(msg.id);
                    onMarkAsRead(msg.id);
                }
            });
        }
    }, [messages, onMarkAsRead]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!messages || messages.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <EmailIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    אין הודעות עדיין
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    התכתבויות עם הלקוח יופיעו כאן
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Messages List */}
            <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}>
                {messages.map((message) => {
                    const isOutgoing = message.direction === 'outgoing';
                    
                    return (
                        <Box
                            key={message.id}
                            sx={{
                                display: 'flex',
                                flexDirection: isOutgoing ? 'row-reverse' : 'row',
                                gap: 1,
                                alignItems: 'flex-start'
                            }}
                        >
                            {/* Avatar */}
                            <Avatar
                                sx={{
                                    bgcolor: isOutgoing ? 'primary.main' : 'secondary.main',
                                    width: 40,
                                    height: 40
                                }}
                            >
                                {getInitials(message, isOutgoing)}
                            </Avatar>

                            {/* Message Content */}
                            <Paper
                                elevation={1}
                                sx={{
                                    maxWidth: '70%',
                                    p: 2,
                                    bgcolor: isOutgoing ? 'primary.light' : 'grey.100',
                                    borderRadius: 2
                                }}
                            >
                                {/* Header */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isOutgoing ? 'אתה' : message.fromEmail}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                                        •
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(message.createdAt)}
                                    </Typography>
                                    {!message.isRead && !isOutgoing && (
                                        <Chip
                                            label="חדש"
                                            size="small"
                                            color="error"
                                            sx={{ ml: 1, height: 20 }}
                                        />
                                    )}
                                </Box>

                                {/* Subject */}
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    {message.subject}
                                </Typography>

                                {/* Body */}
                                <Typography 
                                    variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                    dangerouslySetInnerHTML={{ __html: message.body }}
                                />

                                {/* Files */}
                                {message.files && message.files.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 1 }} />
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                            קבצים מצורפים:
                                        </Typography>
                                        {message.files.map((file) => (
                                            <Box
                                                key={file.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mt: 0.5,
                                                    p: 1,
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 1
                                                }}
                                            >
                                                <AttachFileIcon fontSize="small" />
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    {file.filename}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownloadFile(file.url, file.filename)}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default MessagesThread;

